import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { DateRangeProvider } from './contexts/DateRangeContext';
import ProtectedRoute from './components/ProtectedRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import AdminCockpit from './pages/AdminCockpit';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import PurchaseOrders from './pages/PurchaseOrders';
import POCalendar from './pages/POCalendar';
import PODetails from './pages/PODetails';
import FabricInspectionReports from './pages/FabricInspectionReports';
import FabricInspectionReportDetail from './pages/FabricInspectionReportDetail';
import AddPurchaseOrder from './pages/AddPurchaseOrder';
import EditPurchaseOrder from './pages/EditPurchaseOrder';
import LineInspectionReports from './pages/LineInspectionReports';
import LineInspectionReportDetail from './pages/LineInspectionReportDetail';
import FinalInspectionReports from './pages/FinalInspectionReports';
import FinalInspectionReportDetail from './pages/FinalInspectionReportDetail';
import ContainerInspectionReports from './pages/ContainerInspectionReports';
import ContainerInspectionReportDetail from './pages/ContainerInspectionReportDetail';
import PilotRunReports from './pages/PilotRunReports';
import PilotRunReportDetail from './pages/PilotRunReportDetail';
import UserManagement from './pages/UserManagement';
import AddUser from './pages/AddUser';
import UserDetails from './pages/UserDetails';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import MasterData from './pages/MasterData';
import Analytics from './pages/Analytics';
import FacilityManagement from './pages/FacilityManagement';
import AddFacility from './pages/AddFacility';

const App: React.FC = () => {
  return (
    <AuthProvider>
      <DateRangeProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Admin Cockpit — standalone (own header/nav/footer), super-admin only */}
          <Route path="/admin-cockpit" element={<SuperAdminRoute><AdminCockpit /></SuperAdminRoute>} />

          {/* Protected routes using Layout and ProtectedRoute */}
          <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
          <Route path="/purchase-orders" element={<ProtectedRoute><Layout><PurchaseOrders /></Layout></ProtectedRoute>} />
          <Route path="/purchase-orders/calendar" element={<ProtectedRoute><Layout><POCalendar /></Layout></ProtectedRoute>} />
          <Route path="/purchase-orders/:id" element={<ProtectedRoute><Layout><PODetails /></Layout></ProtectedRoute>} />
          <Route path="/purchase-orders/add" element={<ProtectedRoute><Layout><AddPurchaseOrder /></Layout></ProtectedRoute>} />
          <Route path="/purchase-orders/edit/:id" element={<ProtectedRoute><Layout><EditPurchaseOrder /></Layout></ProtectedRoute>} />

          <Route path="/pilot-run-reports" element={<ProtectedRoute><Layout><PilotRunReports /></Layout></ProtectedRoute>} />
          <Route path="/inspection-report" element={<ProtectedRoute><Layout><FabricInspectionReports /></Layout></ProtectedRoute>} />
          <Route path="/line-inspection-reports" element={<ProtectedRoute><Layout><LineInspectionReports /></Layout></ProtectedRoute>} />
          <Route path="/final-inspection-reports" element={<ProtectedRoute><Layout><FinalInspectionReports /></Layout></ProtectedRoute>} />
          <Route path="/container-inspection-reports" element={<ProtectedRoute><Layout><ContainerInspectionReports /></Layout></ProtectedRoute>} />

          <Route path="/pilot-run-report/:id" element={<ProtectedRoute><Layout><PilotRunReportDetail /></Layout></ProtectedRoute>} />
          <Route path="/inspection-report/:id" element={<ProtectedRoute><Layout><FabricInspectionReportDetail /></Layout></ProtectedRoute>} />
          <Route path="/line-inspection-report/:id" element={<ProtectedRoute><Layout><LineInspectionReportDetail /></Layout></ProtectedRoute>} />
          <Route path="/final-inspection-report/:id" element={<ProtectedRoute><Layout><FinalInspectionReportDetail /></Layout></ProtectedRoute>} />
          <Route path="/container-inspection-report/:id" element={<ProtectedRoute><Layout><ContainerInspectionReportDetail /></Layout></ProtectedRoute>} />

          <Route path="/user-management" element={<ProtectedRoute><Layout><UserManagement /></Layout></ProtectedRoute>} />
          <Route path="/user-management/add" element={<ProtectedRoute><Layout><AddUser /></Layout></ProtectedRoute>} />
          <Route path="/user-management/:id" element={<ProtectedRoute><Layout><UserDetails /></Layout></ProtectedRoute>} />

          <Route path="/master-data" element={<ProtectedRoute><Layout><MasterData /></Layout></ProtectedRoute>} />

          <Route path="/facility-management" element={<ProtectedRoute><Layout><FacilityManagement /></Layout></ProtectedRoute>} />
          <Route path="/facility-management/add" element={<ProtectedRoute><Layout><AddFacility /></Layout></ProtectedRoute>} />
          <Route path="/facility-management/edit/:id" element={<ProtectedRoute><Layout><AddFacility /></Layout></ProtectedRoute>} />

          {/* Legal pages as standalone (no Layout) */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />

          {/* Analytics */}
          <Route path="/analytics" element={<ProtectedRoute><Layout><Analytics /></Layout></ProtectedRoute>} />

          {/* Redirect from root to login or dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
      </DateRangeProvider>
    </AuthProvider>
  );
};

export default App;
